import Input from 'components/common/material-form/Input';
import RaisedButton from 'components/common/material/RaisedButton';
import LoadingBlocking from 'components/loading/LoadingBlocking';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import React from 'react';
import CSSModules from 'react-css-modules';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { connect } from 'react-redux';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import {ValidationsUtils} from 'utils/validationUtils';
import {normalizeSsn, normalizeSsnLast4Digits} from '../../utils/normalizeSsn';
import styles from '../auth.scss';
import SignUpConsent from '../SignUpConsent';
import * as staticComponents from './staticComponents2';
import * as registerConstants from '../authWithOtp/constants2';
import {toggleRememberMe} from "./redux2";
import {replace} from "react-router-redux";
import {datadogRum} from "@datadog/browser-rum";
import {registerOtpTypeUrl} from "../../utils/url/registerOtpTypeUrl";
import {contactUsLink} from "./staticComponents2";
import mixpanelService from '../../services/mixpanel';

const selector = formValueSelector('register');

function validate(values, props) {
  const {intl: {formatMessage}, isFullSsnValidationRequired} = props;
  const errors = {};

  if (!values.email) {
    errors.email = formatMessage({id: 'registration.field.label.emailErrorMissing'});
  }
  else if (!ValidationsUtils.validateEmail(values.email)) {
    errors.email = formatMessage({id: 'registration.field.label.emailError'});
  }

  let ssn4DigitsErrorId;

  if (isFullSsnValidationRequired) {
    if (!values.ssn4Digits) {
      ssn4DigitsErrorId = 'registration.field.label.ssnErrorMissing';
    } else if (values.ssn4Digits.length < 9) {
      ssn4DigitsErrorId = 'registration.field.label.ssnError';
    }
  } else {
    if (!values.ssn4Digits) {
      ssn4DigitsErrorId = 'registration.field.label.ssn4DigitsErrorMissing';
    } else if (values.ssn4Digits.length < 4) {
      ssn4DigitsErrorId = 'registration.field.label.ssn4DigitsError';
    }
  }

  if (ssn4DigitsErrorId) {
    errors.ssn4Digits = formatMessage({id: ssn4DigitsErrorId});
  }

  return errors;
}

@injectIntl
@connect(
  state => ({
    ssn4Digits: selector(state, 'ssn4Digits'),
    email: selector(state, 'email'),
    rememberMe: state.authRoot.register.rememberMe,
    isLoadingRegister: state.authRoot.register.isLoadingRegister,
    isSuccess: state.authRoot.register.isRegisterSuccess,
    isFail: state.authRoot.register.isRegisterFail,
    registerError: state.authRoot.register.registerError,
  }),
  dispatch => ({
    redirect: (path) => dispatch(replace(path)),
    toggleRememberMeChecked: (checked) => dispatch(toggleRememberMe(checked)),
  })
)
@reduxForm({
  form: 'register',
  validate
})
@CSSModules(styles, {allowMultiple: true})
class RegisterForm extends React.Component {

  static propTypes = {
    intl: intlShape,
    submitCallback: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    isLoadingRegister: PropTypes.bool,
    isSuccess: PropTypes.bool,
    isFail: PropTypes.bool,
    registerError: PropTypes.object,
    ssn4Digits: PropTypes.string,
    email: PropTypes.string,
    rememberMe: PropTypes.bool,
    invalid: PropTypes.bool.isRequired,
    toggleRememberMeChecked: PropTypes.func,
    redirect: PropTypes.func,
    isFullSsnValidationRequired: PropTypes.bool,
  };

  @autobind
  handleSubmit() {
    const {ssn4Digits, email} = this.props;
    this.props.submitCallback(ssn4Digits, email);
  }

  renderFooter() {
    const footerValues = {
      signIn: staticComponents.loginLink
    };

    return (
      <div styleName="footer">
        <FormattedMessage id="registration.already" values={footerValues}/>
      </div>
    );
  }

  componentDidUpdate(prevProps) {
    const {isSuccess, isFail, registerError} = this.props;
    if (!prevProps.isSuccess && isSuccess) {
      datadogRum.addUserAction('Sign up start success', {});
      mixpanelService.track('OTP sign up', {step: 'start sign up success'});
      this.props.redirect(registerOtpTypeUrl());
    }
    if (!prevProps.isFail && isFail) {
      datadogRum.addUserAction('Sign up start failed', { registerError });
      mixpanelService.track('OTP sign up', {step: 'start sign up failed'});
    }
  }

  ifFailAccountNotFound() {
    return this.props.isFail && this.props.registerError.code === registerConstants.ENTITY_NOT_FOUND;
  }

  isGenericError() {
    const { registerError } = this.props;
    const sepecificErrors = [registerConstants.ENTITY_NOT_FOUND, registerConstants.ACCOUNT_ALREADY_EXISTS, registerConstants.FAIL_REASON_LOCKED];
    return this.props.isFail && !sepecificErrors.includes(registerError.code);
  }

  render() {
    const {intl: {formatMessage}, isFullSsnValidationRequired} = this.props;

    if (this.props.isFail && this.props.registerError.code === registerConstants.ACCOUNT_ALREADY_EXISTS) {
      const failValues = {
        contactUsLink: staticComponents.contactUsLink,
        tryAgainLink: staticComponents.registerTryAgain,
        loginLink: staticComponents.loginLink,
        resetLink: staticComponents.resetLink
      };

      return (
        <div styleName="content">
          <div styleName="title already-exist-title" data-at="failureTitle"><FormattedMessage id="auth.title.alreadyExists"/></div>
          <br/>
          <div styleName="subtitle" data-at="failureSubtitle">
            <FormattedMessage id="auth.message.alreadyExists" values={failValues}/>
          </div>
        </div>
      );
    }

    return (
      <div styleName="content">
        {this.props.isLoadingRegister && <LoadingBlocking /> }
        <div data-at="regTitle" styleName="partition title"><FormattedMessage id="registration.titleSignUp"/></div>
        <div styleName="partition">
          <div styleName="subtitle" data-at="subtitle">
            <FormattedMessage id="registration.pleaseEnterDetails" />
          </div>
        </div>
        {
          this.ifFailAccountNotFound() &&
          <div data-at={'noAccountFoundForRegistration'} styleName="noAccount">
            <FormattedMessage id="registration.field.label.noAccount"/>
            <br/>
            <br/>
            <FormattedMessage id="registration.field.label.noAccountTryAgain" values={{contactUsLink}}/>
          </div>
        }
        {
          this.isGenericError() &&
          <div data-at={'genericError'} styleName="noAccount">
            <FormattedMessage id="common.technicalError"/>
          </div>
        }
        {
          isFullSsnValidationRequired && <div styleName="noAccount">
            <FormattedMessage id="registration.error.fullSSNRequired"/>
          </div>
        }
        <div>
          <Field data-at="emailInput" name="email" component={Input} label={formatMessage({id: 'registration.field.label.email'})} fullWidth={true}/>
        </div>
        { 
          isFullSsnValidationRequired && <div>
            <Field data-at="ssn4DigitsInput" name="ssn4Digits" component={Input} label={formatMessage({id: 'registration.field.label.ssn'})} fullWidth={true} normalize={normalizeSsn}/>
          </div>
        }
        { 
          !isFullSsnValidationRequired && <div>
            <Field data-at="ssn4DigitsInput" name="ssn4Digits" component={Input} label={formatMessage({id: 'registration.field.label.ssn4Digits'})} fullWidth={true} normalize={normalizeSsnLast4Digits}/>
          </div>
        }
        
        {/*<div styleName="rememberMe">*/}
        {/*  <input type="checkbox" onClick={(event) => {*/}
        {/*    this.props.toggleRememberMeChecked(event.target.checked);*/}
        {/*  }}/>*/}
        {/*  <span>Remember this device</span>*/}
        {/*</div>*/}
        <br/>
        <div>
          <SignUpConsent buttonConfirmText={staticComponents.agreeAndSignUpText}/>
          <br/>
          <RaisedButton data-at="agreeAndSignupBtn"
                        onClick={this.handleSubmit}
                        disabled={this.props.invalid}
                        fullWidth={true}
                        primary={true}
                        label={formatMessage({id: 'registration.button.continue'})}/>
        </div>
        <br/>
        {this.renderFooter()}
      </div>
    );

  }
}
export default RegisterForm;



// WEBPACK FOOTER //
// ./src/auth/authWithOtp/RegisterForm2.js